import { forwardRef } from 'react';

import {
  browserTimeZone,
  fromLocalInput,
  toLocalInput,
} from '@/shared/utils/local-instant/local-instant.util';
import { cn } from '@/shared/utils/cn/cn.util';

import { fieldVariants } from '../field/field.tokens';

import { type DateTimeInputProps } from './DateTimeInput.types';

/**
 * A date **and** a time, on the native `<input type="datetime-local">`.
 *
 * The same argument `DatePicker` makes and for the same reasons: the native
 * control already carries the calendar popover, the locale-appropriate ordering,
 * the mobile wheels and full keyboard operation, and a hand-built one has to
 * re-earn all of it and usually gets RTL subtly wrong. The portal ships no
 * calendar library.
 *
 * ## The value is an INSTANT; the control is the only place a wall clock exists
 *
 * The native element speaks a zone-less wall clock (`2026-09-01T12:00`) and every
 * contract speaks an instant, so the conversion has to live somewhere. It lives
 * **here**, once, over `local-instant.util` — never at a call site, where the
 * zone it depends on would be re-derived by each of them and got wrong by one of
 * them. `min`/`max` are instants for the same reason and are converted on the way
 * in: a bound in a different space from the value it bounds is a bound nobody can
 * reason about.
 *
 * Pure presentation (U5): it holds no state and decides nothing. Everything that
 * could be wrong about a zone is in the util, with a spec that walks four of them.
 */
export const DateTimeInput = forwardRef<HTMLInputElement, DateTimeInputProps>(
  function DateTimeInput(
    { className, value, onValueChange, min, max, isInvalid = false, timeZone, ...props },
    ref,
  ) {
    // Resolved once per render rather than defaulted three times: the value and
    // both bounds must be read in the SAME zone, and three separate calls to the
    // browser would be three chances for them not to be.
    const zone = timeZone ?? browserTimeZone();

    return (
      <input
        ref={ref}
        type="datetime-local"
        // `?? ''` keeps the input controlled — `toLocalInput` already answers `''`
        // for an absent or unparseable value, so there is nothing further to
        // guard here.
        value={toLocalInput(value ?? null, zone)}
        min={toLocalInput(min ?? null, zone) || undefined}
        max={toLocalInput(max ?? null, zone) || undefined}
        onChange={(event) => {
          // A cleared native field reports `''`, and a half-typed one reports it
          // too — `fromLocalInput` answers `null` for both, so an optional
          // instant can genuinely be removed rather than saved as a fragment.
          onValueChange(fromLocalInput(event.target.value, zone));
        }}
        aria-invalid={isInvalid ? true : undefined}
        className={cn(fieldVariants(), 'items-center', className)}
        {...props}
      />
    );
  },
);
